/////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//
// LearningSiteDatabase class
// Responsible for managing App
// sessions (collecting and storing info)
// Communicating with the analytics db
//
// (C) Robotical 2023
//
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import randomHashGenerator from "../helpers/randomHashGenerator";
import { Database } from "./Database";
import { downloadedLessosTitleBuilder, searchTermTitleBuilder, titleBuilder, usedFilterTitleBuilder, visitedLessosTitleBuilder } from "../path-builders/learning-site-path-builder";
import Bowser from "bowser"; // COMMENT OUT WHEN TESTING
const DEVICE_ID_COOKIE_KEY = "deviceId";
const SESSION_ID_COOKIE_KEY = "analytics_session_id";
export default class LearningSiteDatabase extends Database {
    constructor(config, databaseName) {
        super(config, databaseName);
        this.dataToStore = {
            firstTimestamp: "",
            lastTimestamp: "",
            deviceId: "",
            UsedFilters: {},
            SearchTerms: {},
            sessionId: "",
            browser: "",
            isActive: false,
            platformType: "",
            osName: "",
            timezone: "",
        };
        this.firstTimestamp = null;
        this.lastTimestamp = null;
        this.heartbeat = null;
        this.heartbeatInterval = 60000;
        this.maxIntervalBetweenSessions = 3600000; // 1 hour
        this.ACTIVE_SESSION_TIME_POINT = 300000; // 5 minutes
    }
    addDataToSession(key, value) {
        this.dataToStore[key] = value;
    }
    async heartbeatHandler() {
        // decide if we should start a new session or
        // we continue
        const newLastTimestamp = new Date();
        this.decideIfActive(newLastTimestamp);
        if (this.lastTimestamp &&
            newLastTimestamp.getTime() - this.lastTimestamp.getTime() > this.maxIntervalBetweenSessions) {
            // the app was inactive long enough
            // removing the old session and starting a new one
            try {
                sessionStorage.removeItem(SESSION_ID_COOKIE_KEY);
            }
            catch (e) {
                console.log("Error removing session id from session storage");
            }
            this.stopHeartbeat();
            this.startSession();
        }
        else {
            // we are still in the same session, update heartbeat
            this.lastTimestamp = newLastTimestamp;
            const dbPath = titleBuilder(this.dataToStore.deviceId, this.dataToStore.sessionId);
            // checking if the dbPath exists in the database, if not then we won't send a new lastTimestamp.
            // This is probably a development artefact
            const pathExist = await this.exists(dbPath);
            if (pathExist && this.dataToStore.sessionId) {
                // send heartbeat only if session already exists
                await this.update({ lastTimestamp: this.lastTimestamp }, dbPath);
            }
        }
    }
    async getFirstTimestamp() {
        if (!this.dataToStore.sessionId || !this.dataToStore.deviceId)
            return;
        const dbPath = titleBuilder(this.dataToStore.deviceId, this.dataToStore.sessionId);
        const data = await this.get(dbPath);
        if (data.firstTimestamp)
            return new Date(data.firstTimestamp);
        return null;
    }
    async decideIfActive(newLastTimestamp) {
        // if a session is active for more than 1 minute (diff between firstTimestamp and lastTimestamp > 1 minute)
        // then we consider it active
        if (!this.dataToStore["sessionId"])
            return;
        if (this.dataToStore["isActive"])
            return;
        if (!this.firstTimestamp) {
            const firstTimestamp = await this.getFirstTimestamp();
            if (!firstTimestamp)
                return;
            this.firstTimestamp = firstTimestamp;
        }
        const diff = newLastTimestamp.getTime() - this.firstTimestamp.getTime();
        if (diff > this.ACTIVE_SESSION_TIME_POINT) {
            this.dataToStore["isActive"] = true;
            await this.update({ isActive: true }, titleBuilder(this.dataToStore.deviceId, this.dataToStore.sessionId));
        }
    }
    async setIsActive(isActive) {
        if (!this.dataToStore["sessionId"])
            return;
        if (this.dataToStore["isActive"] === isActive)
            return;
        this.dataToStore["isActive"] = isActive;
        await this.update({ isActive }, titleBuilder(this.dataToStore.deviceId, this.dataToStore.sessionId));
    }
    async setHeartbeat() {
        // every this.heartbeatInterval we sent a new timestamp
        // to the db. In that way we can estimate the time
        // marty was used.
        // Heartbeat is only sent when the app is active.
        // We will take advantage of that and we will use
        // it to decide if we should create a new session
        // or no (if the app was inactive for more than say
        // an hour, then we can categorise the new activity
        // as a new session).
        if (!this.heartbeat) {
            // if there is no heartbeat we just
            // set it up
            this.heartbeat = setInterval(this.heartbeatHandler.bind(this), this.heartbeatInterval);
        }
    }
    stopHeartbeat() {
        // stopping repeatedly sending msgs to the db
        if (this.heartbeat) {
            clearInterval(this.heartbeat);
        }
        this.lastTimestamp = null;
        this.heartbeat = null;
    }
    prepareInitialData(deviceId, sessionId) {
        this.addDataToSession("deviceId", deviceId);
        this.addDataToSession("sessionId", sessionId);
        this.lastTimestamp = new Date();
        this.firstTimestamp = this.lastTimestamp;
        this.addDataToSession("firstTimestamp", this.lastTimestamp.toISOString());
        this.addDataToSession("lastTimestamp", new Date().toISOString());
        this.addDataToSession("timezone", Intl.DateTimeFormat().resolvedOptions().timeZone);
        const browserInfo = this.getBrowserInfo();
        this.addDataToSession("browser", browserInfo.browserName);
        this.addDataToSession("platformType", browserInfo.platformType);
        this.addDataToSession("osName", browserInfo.osName);
    }
    getBrowserInfo() {
        // return { browserName: "", platformType: "", osName: ""};
        // COMMENT OUT WHEN TESTING
        const browser = Bowser.getParser(window.navigator.userAgent);
        const browserInfo = browser.getBrowser();
        // Getting the platform information
        const browserName = browserInfo.name; // "Chrome", "Firefox", "Safari", etc.
        const platformType = browser.getPlatformType(); // "desktop", "tablet", or "mobile"
        const osName = browser.getOSName(); // "Windows", "macOS", "Android", "iOS", etc.
        return {
            browserName,
            platformType,
            osName,
        };
    }
    async startSession() {
        if (!this.db)
            await this.initialize();
        const sessionId = this.getOrSetSessionId();
        // check if the session already exists
        // if it does, then we don't need to create a new one
        const deviceId = this.getOrSetDeviceId();
        const dbPath = titleBuilder(deviceId, sessionId);
        const pathExist = await this.exists(dbPath);
        if (pathExist) {
            // the session already exists, we just need to set the heartbeat again
            this.addDataToSession("deviceId", deviceId);
            this.addDataToSession("sessionId", sessionId);
            this.addDataToSession("lastTimestamp", new Date().toISOString());
            this.setHeartbeat();
            return;
        }
        this.prepareInitialData(deviceId, sessionId);
        await this.set(this.dataToStore, titleBuilder(deviceId, sessionId));
        this.setHeartbeat();
    }
    getOrSetDeviceId() {
        let deviceId = "";
        try {
            deviceId = localStorage.getItem(DEVICE_ID_COOKIE_KEY);
        }
        catch (e) {
            console.log("Error getting device id from local storage");
        }
        if (!deviceId) {
            deviceId = randomHashGenerator();
            try {
                localStorage.setItem(DEVICE_ID_COOKIE_KEY, deviceId);
            }
            catch (e) {
                console.log("Error setting device id to local storage");
            }
        }
        return deviceId;
    }
    getOrSetSessionId() {
        let sessionId = "";
        try {
            sessionId = sessionStorage.getItem(SESSION_ID_COOKIE_KEY);
        }
        catch (e) {
            console.log("Error getting session id from session storage");
        }
        if (!sessionId) {
            sessionId = randomHashGenerator();
            try {
                sessionStorage.setItem(SESSION_ID_COOKIE_KEY, sessionId);
            }
            catch (e) {
                console.log("Error setting session id to session storage");
            }
        }
        return sessionId;
    }
    async storeSearchTerm(searchTerm) {
        if (!this.dataToStore.sessionId || !this.dataToStore.deviceId)
            return;
        const dataToStore = {
            searchTerm: searchTerm,
            sessionId: this.dataToStore.sessionId,
            deviceId: this.dataToStore.deviceId,
        };
        const searchTermAsAPath = searchTerm.replace(/[.#$[\]]/g, "_");
        const searchTermId = searchTermAsAPath + "-" + randomHashGenerator();
        await this.set(dataToStore, searchTermTitleBuilder(this.dataToStore.deviceId, this.dataToStore.sessionId, searchTermId));
        return dataToStore;
    }
    async storeUsedFilter(filterName, filterCategory) {
        if (!this.dataToStore.sessionId || !this.dataToStore.deviceId)
            return;
        const dataToStore = {
            filterName: filterName,
            sessionId: this.dataToStore.sessionId,
            deviceId: this.dataToStore.deviceId,
            filterCategory: filterCategory,
        };
        const filterNameAsAPath = filterName.replace(/[.#$[\]]/g, "_");
        const filterNameId = filterNameAsAPath + "-" + randomHashGenerator();
        await this.set(dataToStore, usedFilterTitleBuilder(this.dataToStore.deviceId, this.dataToStore.sessionId, filterNameId));
        return dataToStore;
    }
    async storeVisitedLesson(lessonId) {
        if (!this.dataToStore.sessionId || !this.dataToStore.deviceId)
            return {};
        const dataToStore = {
            lessonId: lessonId,
            timestamp: new Date().toISOString(),
            sessionId: this.dataToStore.sessionId,
            deviceId: this.dataToStore.deviceId,
        };
        const lessonHashedId = lessonId + "-" + randomHashGenerator();
        await this.set(dataToStore, visitedLessosTitleBuilder(this.dataToStore.deviceId, this.dataToStore.sessionId, lessonHashedId));
        return dataToStore;
    }
    async storeDownloadedLesson(lessonId) {
        if (!this.dataToStore.sessionId)
            return {};
        const dataToStore = {
            lessonId: lessonId,
            timestamp: new Date().toISOString(),
            sessionId: this.dataToStore.sessionId,
            deviceId: this.dataToStore.deviceId,
        };
        const lessonHashedId = lessonId + "-" + randomHashGenerator();
        await this.set(dataToStore, downloadedLessosTitleBuilder(this.dataToStore.deviceId, this.dataToStore.sessionId, lessonHashedId));
        return dataToStore;
    }
}
